import React, { useCallback, useRef } from "react";
import { Button, classnames, Icon, PlainButton } from "@culturehq/components";

import { useDocumentEvent, useDoor } from "app/hooks";

import { useProfile } from "app/context";
import { containedBy, Trans } from "app/utils";

import styles from "./styles.module.scss";
import StoryCopyLink from "./StoryCopyLink";
import StoryShareSlack from "./StoryShareSlack";
import StoryShareUsers from "./StoryShareUsers";

const ShareLinks = ({ imageToShow, onClose, storyQuestion }) => {
  const { profile } = useProfile();

  return (
    <ul className="dropdown-menu">
      <li>
        <StoryCopyLink
          isPrompt={!storyQuestion.childStoryQuestions.length}
          onClose={onClose}
          storyQuestion={storyQuestion}
        >
          {onTrigger => (
            <PlainButton onClick={onTrigger}>
              <Icon icon="link-wider" /><Trans>Copy Link</Trans>
            </PlainButton>
          )}
        </StoryCopyLink>
      </li>
      {profile.organization.slackIntegration && (
        <li>
          <StoryShareSlack
            storyQuestion={storyQuestion}
            imageToShow={imageToShow}
            onClose={onClose}
          >
            {onTrigger => (
              <PlainButton onClick={onTrigger}>
                <Icon icon="slack" /><Trans>Send in Slack</Trans>
              </PlainButton>
            )}
          </StoryShareSlack>
        </li>
      )}
      <li>
        <StoryShareUsers
          storyQuestion={storyQuestion}
          imageToShow={imageToShow}
          onClose={onClose}
        >
          {onTrigger => (
            <PlainButton onClick={onTrigger}>
              <Icon icon="ios-people-outline" /><Trans>Send to specific users</Trans>
            </PlainButton>
          )}
        </StoryShareUsers>
      </li>
    </ul>
  );
};

const QuickLinksButton = ({
  className, children, inline, onClose, open, onToggle, closeOnOutsideClick, nextToMenu, filledIcon
}) => {
  const containerRef = useRef();

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "chq-mdl") && containerRef.current
        && !containerRef.current.contains(event.target) && closeOnOutsideClick) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    "QuickOptions--Actions",
    styles.quick,
    styles[className],
    nextToMenu && styles["menu-next"],
    open && styles["quick-open"]
  );

  return (
    <div ref={containerRef} className={classList}>
      {inline
        ? (
          <PlainButton aria-label="Share" className="QuickOptions--Send_btn" onClick={onToggle}><Icon icon="send-filled" /></PlainButton>
        ) : (
          <Button aria-label="Share" className="share-button" onClick={onToggle} inverted>
            <Icon icon={filledIcon ? "send-filled" : "send"} className={styles.svg} /><Trans>Send Prompt</Trans>
          </Button>
        )}
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const ShareQuickLinks = ({
  imageToShow, inline = false, storyQuestion, className, closeOnOutsideClick = false,
  nextToMenu = false, filledIcon = false
}) => {
  const { open, onClose, onToggle } = useDoor(false);

  if (!storyQuestion) {
    return (
      <StoryCopyLink
        isPrompt={!storyQuestion.childStoryQuestions.length}
        onClose={onClose}
        storyQuestion={storyQuestion}
      >
        {onTrigger => (
          <PlainButton aria-label="Share" className="share-button" onClick={onTrigger}>
            <Icon icon="share" />
          </PlainButton>
        )}
      </StoryCopyLink>
    );
  }

  return (
    <>
      <QuickLinksButton
        open={open}
        onClose={onClose}
        onToggle={onToggle}
        inline={inline}
        className={className}
        closeOnOutsideClick={closeOnOutsideClick}
        filledIcon={filledIcon}
        nextToMenu={nextToMenu}
      >
        <ShareLinks
          imageToShow={imageToShow}
          storyQuestion={storyQuestion}
          onClose={onClose}
        />
      </QuickLinksButton>
    </>
  );
};

export default ShareQuickLinks;
